import React, { useMemo } from 'react';
import {
  FilterOption,
  GetPractitionerResourceSearchDocument,
} from '__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import { useAppendQueryString } from '../useAppendQueryString';
import { LinkTabs, LinkTab } from '../LinkTabs';
import styled from 'styled-components';
import groupedBy from 'lodash/fp/groupBy';

const persistedQueryVariables = [
  'query',
  'textFilters',
  'optionFilters',
  'sort',
  'outofplan',
];

const groupByFilterValue = groupedBy<FilterOption>('filterValue');

export const doctypes = [
  'doctype_workflows',
  'doctype_how-to-guides',
  'doctype_standard-documents',
  'doctype_strategy-notes',
  'doctype_charts-and-checklists',
] as const;

export interface PractitionerSearchDoctypeTabsProps {
  counts: Record<string, number> | undefined;
}

export function PractitionerSearchDoctypeTabs({
  counts,
}: PractitionerSearchDoctypeTabsProps) {
  const {
    data: { searchFilter },
  } = useSuspenseQuery(GetPractitionerResourceSearchDocument);

  const appendQueryString = useAppendQueryString();

  const filterValueGroups = useMemo(
    () => groupByFilterValue(searchFilter?.children),
    [searchFilter],
  );

  return (
    <StyledLinkTabs>
      <StyledLinkTab
        to={appendQueryString('/search/practitioner', persistedQueryVariables)}
        queryVariables={['query', 'doctype']}
      >
        All Types
      </StyledLinkTab>

      {doctypes.map(doctype =>
        filterValueGroups[doctype].map(filter => (
          <StyledLinkTab
            key={filter!.filterValue!}
            to={appendQueryString(
              '/search/practitioner',
              persistedQueryVariables,
              {
                doctype: filter!.filterValue!,
              },
            )}
            queryVariables={['query', 'doctype']}
            disabled={counts === undefined ? true : counts[doctype] === 0}
          >
            {filter?.name}
          </StyledLinkTab>
        )),
      )}
    </StyledLinkTabs>
  );
}

const StyledLinkTabs = styled(LinkTabs)`
  list-style: none;
  display: flex;
  gap: 10px;
  border-bottom: solid 1px #dedddc;
  position: relative;
  padding: 12px 70.5px 0;
`;

const StyledLinkTab = styled(LinkTab)`
  background-color: #f5f5f5;
  border-radius: 3px 3px 0 0;
  color: var(--COLOR_BLACK);
  font-size: 15px;
  font-weight: bold;
  padding: 11.5px 18.5px;
  a,
  a:visited {
    color: var(--COLOR_BLACK);
  }
  &.active {
    background-color: white;
    border: solid 1px #dedddc;
    border-bottom: solid 1px white;
    bottom: -1px;
    color: var(--COLOR_BLACK);
    margin-top: -1px;
    position: relative;
  }
`;
